import { notifications } from '@mantine/notifications';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useInvalidateUnhandledClients } from 'app/Private/Clients/Detail/UnhandledInvoicesTab/use-invalidate-unhandled-clients';
import type { AxiosInstance } from 'axios';
import { z } from 'zod/v4';
import { type ApiError, useAxiosInstance } from './utils';
import { addActionNameToMutationResult } from './utils/add-action-name-to-mutation-result';

const CreatePaymentPlanVariablesSchema = z.object({
  debtorId: z.string(),
  workflowId: z.string(),
  withFees: z.boolean(),
  invoiceIds: z.array(z.string()),
  creditNoteIds: z.array(z.string()),
  thirdPartyPaymentIds: z.array(z.string()),
  accountingPaymentIds: z.array(z.string()),
  paymentStepsAttributes: z.array(
    z.object({
      amount: z.number(),
      dueDate: z.string(),
    }),
  ),
});

export type CreatePaymentPlanVariables = z.infer<typeof CreatePaymentPlanVariablesSchema>;

const CreatePaymentPlanResponseSchema = z.object({
  paymentPlanId: z.coerce.string(),
  message: z.string(),
});

type CreatePaymentPlanResponse = z.infer<typeof CreatePaymentPlanResponseSchema>;

export async function createPaymentPlanFn(
  axiosInstance: Promise<AxiosInstance>,
  data: CreatePaymentPlanVariables,
) {
  const instance = await axiosInstance;
  const response = await instance.post(
    'payment_plans',
    CreatePaymentPlanVariablesSchema.parse(data),
  );

  return CreatePaymentPlanResponseSchema.parse(response.data);
}

export function useCreatePaymentPlan() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();
  const invalidateUnhandledClients = useInvalidateUnhandledClients();

  const mutation = useMutation<CreatePaymentPlanResponse, ApiError, CreatePaymentPlanVariables>({
    mutationFn: data => createPaymentPlanFn(axiosInstance, data),
    onSuccess: (data, { debtorId }) => {
      notifications.show({
        message: data.message,
        color: 'green',
      });
      client.invalidateQueries({ queryKey: ['payment-plans'] });
      invalidateUnhandledClients(debtorId);
    },
  });

  return addActionNameToMutationResult<
    CreatePaymentPlanResponse,
    ApiError,
    CreatePaymentPlanVariables,
    'createPaymentPlan'
  >('createPaymentPlan', mutation);
}
